import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// All requests send cookies (sr_session / sr_refresh).
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ---- LiveKit ----
export async function getLiveKitConfig() {
  try {
    const { data } = await api.get("/livekit/config");
    return { configured: true, url: data.url };
  } catch (e) {
    if (e?.response?.status === 503) {
      return {
        configured: false,
        error: e.response.data?.error || "LIVEKIT_NOT_CONFIGURED",
        message: e.response.data?.message,
      };
    }
    throw e;
  }
}

export async function getHealth() {
  const { data } = await api.get("/health");
  return data; // { ok, livekit_configured, discord_configured }
}

// ---- Auth ----
export async function authSignup({ email, password, display_name }) {
  const { data } = await api.post("/auth/signup", { email, password, display_name });
  return data;
}

export async function authLogin({ email, password }) {
  const { data } = await api.post("/auth/login", { email, password });
  return data;
}

export async function authLogout() {
  await api.post("/auth/logout");
}

export async function authMe() {
  const { data } = await api.get("/auth/me");
  return data.user;
}

export async function authRefresh() {
  const { data } = await api.post("/auth/refresh");
  return data;
}

export async function recoverUsername(email) {
  const { data } = await api.post("/auth/recovery/username", { email });
  return data;
}

export async function requestPasswordReset(email) {
  const { data } = await api.post("/auth/recovery/password", { email });
  return data;
}

export async function confirmPasswordReset(token, newPassword) {
  const { data } = await api.post("/auth/recovery/password/confirm", { token, new_password: newPassword });
  return data;
}

export async function changePassword(currentPassword, newPassword) {
  const { data } = await api.post("/auth/password/change", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data;
}

export async function discordStart(returnTo = "/") {
  try {
    const { data } = await api.get("/auth/discord/start", { params: { return_to: returnTo } });
    return { configured: true, auth_url: data.auth_url };
  } catch (e) {
    if (e?.response?.status === 503) {
      return {
        configured: false,
        error: e.response.data?.error || "DISCORD_NOT_CONFIGURED",
        message: e.response.data?.message,
      };
    }
    throw e;
  }
}

export async function discordLinkStart() {
  try {
    const { data } = await api.get("/auth/discord/link/start");
    return { configured: true, auth_url: data.auth_url };
  } catch (e) {
    if (e?.response?.status === 503) {
      return {
        configured: false,
        error: e.response.data?.error || "DISCORD_NOT_CONFIGURED",
        message: e.response.data?.message,
      };
    }
    throw e;
  }
}

// ---- Rooms ----
export async function createRoom({ name, duration_hours, invite_only, mode, max_participants, max_live_cameras, always_camera_on, super_admin_unlocks_room_mode }) {
  const { data } = await api.post("/rooms", {
    name, duration_hours, invite_only,
    // Grid/Festival are only honored server-side when caller is super-admin with
    // platform_role=super_admin AND super_admin_unlocks_room_mode=true.
    mode, max_participants, max_live_cameras, always_camera_on, super_admin_unlocks_room_mode,
  });
  return data;
}

// R18-5 — Camera Request endpoints (audience → live wall).
export async function requestCamera(roomId, note) {
  const { data } = await api.post(`/rooms/${roomId}/camera/request`, { note });
  return data;
}
export async function listCameraRequests(roomId) {
  const { data } = await api.get(`/rooms/${roomId}/camera/requests`);
  return data;
}
export async function approveCameraRequest(roomId, requestId) {
  const { data } = await api.post(`/rooms/${roomId}/camera/${requestId}/approve`);
  return data;
}
export async function denyCameraRequest(roomId, requestId) {
  const { data } = await api.post(`/rooms/${roomId}/camera/${requestId}/deny`);
  return data;
}
export async function inviteToCamera(roomId, targetUserId, durationMinutes) {
  const { data } = await api.post(`/rooms/${roomId}/camera/invite`, {
    target_user_id: targetUserId, duration_minutes: durationMinutes || null,
  });
  return data;
}
// R18-5 — Per-tile revoke by target user_id (resolves the most recent
// approved/invited request server-side).
export async function revokeCameraByUser(roomId, targetUserId) {
  const { data } = await api.post(`/rooms/${roomId}/camera/user/${targetUserId}/revoke`);
  return data;
}

// Revoke by explicit request_id (kept for direct admin / tray flows).
export async function revokeCameraRequest(roomId, requestId) {
  const { data } = await api.post(`/rooms/${roomId}/camera/${requestId}/revoke`);
  return data;
}

// R18-7 — Frontend's currently-subscribed video count, posted ~60s by Room.jsx.
export async function postSubscribeTick(roomId, count, clientId) {
  const { data } = await api.post(`/rooms/${roomId}/subscribe-tick`, {
    active_subscribed_video_count: Math.max(0, count | 0),
    client_id: clientId || null,
  });
  return data;
}

// R18-9 — DJ heartbeat (caller MUST be current DJ; backend 403 otherwise).
export async function djHeartbeat(roomId) {
  const { data } = await api.post(`/rooms/${roomId}/dj-heartbeat`);
  return data;
}

// R18-9 — Host manually extends an idle room.
export async function extendRoom(roomId) {
  const { data } = await api.post(`/rooms/${roomId}/extend`);
  return data;
}

// R18-4 — Roster of ALL members currently in the room (publishers AND
// non-publishers), powering the Crowd Wall's audience-avatar tiles.
export async function getRoomRoster(roomId) {
  const { data } = await api.get(`/rooms/${roomId}/roster`);
  return data;
}

export async function listMyRooms({ includeCohost = false } = {}) {
  const { data } = await api.get("/rooms", { params: includeCohost ? { include_cohost: true } : {} });
  return data;
}

// R16-C — "My Archive" support (paginated list, cleanup, restore).
export async function listMyArchivedRooms() {
  const { data } = await api.get("/me/rooms", { params: { room_status: "archived" } });
  return data?.rooms || [];
}

export async function cleanupMyRoom(roomId) {
  const { data } = await api.post(`/me/rooms/${roomId}/cleanup`);
  return data;
}

export async function restoreMyRoom(roomId) {
  const { data } = await api.post(`/me/rooms/${roomId}/restore`);
  return data;
}

export async function getRoom(roomId) {
  const { data } = await api.get(`/rooms/${roomId}`);
  return data;
}

// Resolves a high-entropy invite URL without exposing the internal room ID in
// the participant-facing address bar.
export async function resolveRoomInvite(inviteCode) {
  const { data } = await api.get(`/rooms/invite/${encodeURIComponent(inviteCode)}`);
  return data;
}

export async function endRoom(roomId) {
  const { data } = await api.post(`/rooms/${roomId}/end`);
  return data;
}

export async function joinRoom(roomId) {
  const { data } = await api.post(`/rooms/${roomId}/join`);
  return data; // { status: "approved" | "waiting", ... }
}

export async function getRoomToken(roomId) {
  try {
    const { data } = await api.post(`/rooms/${roomId}/token`);
    return { ok: true, ...data };
  } catch (e) {
    if (e?.response?.status === 401) return { ok: false, kind: "anonymous", error: e.response.data };
    if (e?.response?.status === 503) return { ok: false, kind: "lk_unconfigured", error: e.response.data };
    if (e?.response?.status === 403) {
      const body = e.response.data || {};
      if (body.error === "CONSENT_REQUIRED") return { ok: false, kind: "consent_required", error: body };
      if (body.detail === "PASSWORD_CHANGE_REQUIRED" || body.error === "PASSWORD_CHANGE_REQUIRED") {
        return { ok: false, kind: "password_change_required", error: body };
      }
      if (body.error === "GUEST_TOKEN_WRONG_ROOM" || body.error === "GUEST_EVENT_EXPIRED") {
        return { ok: false, kind: "guest_entry_required", error: body };
      }
      return { ok: false, kind: "forbidden", error: body };
    }
    if (e?.response?.status === 409) return { ok: false, kind: "already_in_room", error: e.response.data };
    if (e?.response?.status === 410) {
      // Differentiate ROOM_ENDED (host can reopen), ROOM_EXPIRED (terminal),
      // and room_full (R18-10 — cap hit; can retry).
      const body = e.response.data || {};
      if (body.error === "room_full") {
        return { ok: false, kind: "room_full", error: body, cap: body.cap };
      }
      const kind = body.error === "ROOM_ENDED" ? "ended" : body.error === "ROOM_CLOSED" ? "closed" : "expired";
      return { ok: false, kind, error: body, ended_at: body.ended_at, can_reopen: false };
    }
    throw e;
  }
}

// Phase 2.2 — single-active-room enforcement client helpers
export async function leaveSession(roomId) {
  const { data } = await api.post("/sessions/leave", roomId ? { room_id: roomId } : {});
  return data;
}

export async function sessionHeartbeat(roomId) {
  try {
    const { data } = await api.post("/sessions/heartbeat", { room_id: roomId });
    return data;
  } catch { return { ok: false }; }
}

// Phase 2.2 — DJ assignment
export async function djAction(roomId, action, targetUserId) {
  const { data } = await api.post(`/rooms/${roomId}/dj`, { action, target_user_id: targetUserId });
  return data;
}

// Phase 2.2 — Transfer host role to an existing cohost
export async function transferHost(roomId, targetUserId) {
  const { data } = await api.post(`/rooms/${roomId}/transfer-host`, { target_user_id: targetUserId });
  return data;
}

export async function setMyRoomDisplayName(roomId, displayName) {
  const { data } = await api.post(`/rooms/${roomId}/my-display-name`, { display_name: displayName });
  return data;
}

export async function refreshRoomToken(roomId) {
  return getRoomToken(roomId).then((r) => {
    if (!r.ok) return r;
    // refresh endpoint returns same shape
    return r;
  });
}

export async function moderate(roomId, { target_user_id, action }) {
  const { data } = await api.post(`/rooms/${roomId}/moderate`, { target_user_id, action });
  return data;
}

// ---- Phase 2.3 — host room controls ----
export async function lockRoom(roomId, locked) {
  const { data } = await api.post(`/rooms/${roomId}/lock`, { locked });
  return data;
}
export async function setAlwaysCameraOn(roomId, alwaysCameraOn) {
  const { data } = await api.post(`/rooms/${roomId}/camera-policy`, { always_camera_on: alwaysCameraOn });
  return data;
}
export async function startClosing(roomId) {
  const { data } = await api.post(`/rooms/${roomId}/closing/start`, {});
  return data;
}
export async function cancelClosing(roomId) {
  const { data } = await api.post(`/rooms/${roomId}/closing/cancel`, {});
  return data;
}
export async function renameParticipant(roomId, targetUserId, newName) {
  const { data } = await api.post(`/rooms/${roomId}/rename`,
    { target_user_id: targetUserId, new_name: newName });
  return data;
}
export async function reopenRoom(roomId) {
  const { data } = await api.post(`/rooms/${roomId}/reopen`, {});
  return data;
}

// ---- Phase 2.5 — Consent + GDPR ----
export const CONSENT_VERSION = "1.0";
export async function recordConsent(version = CONSENT_VERSION) {
  const { data } = await api.post("/me/consent", { accepted: true, version });
  return data;
}
export async function getPersonalDataReport() {
  const { data } = await api.get("/me/data/report");
  return data;
}

export async function requestDataDeletion() {
  const { data } = await api.post("/me/data/delete-request", {});
  return data;
}
export async function cancelDataDeletion() {
  const { data } = await api.post("/me/data/delete-cancel", {});
  return data;
}

// ---- Phase 3 — Chat ----
export async function listChatThreads(roomId) {
  const { data } = await api.get(`/rooms/${roomId}/chat/threads`);
  return data;
}
export async function listPublicChat(roomId, { limit = 50, before } = {}) {
  const params = { limit };
  if (before) params.before = before;
  const { data } = await api.get(`/rooms/${roomId}/chat/public`, { params });
  return data;
}
export async function postPublicChat(roomId, body) {
  const { data } = await api.post(`/rooms/${roomId}/chat/public`, { body });
  return data;
}
export async function listChatMessages(roomId, threadId, { limit = 50, before } = {}) {
  const params = { limit };
  if (before) params.before = before;
  const { data } = await api.get(`/rooms/${roomId}/chat/${threadId}/messages`, { params });
  return data;
}
export async function postChatMessage(roomId, threadId, body) {
  const { data } = await api.post(`/rooms/${roomId}/chat/${threadId}/messages`, { body });
  return data;
}
export async function editChatMessage(roomId, messageId, body) {
  const { data } = await api.patch(`/rooms/${roomId}/chat/messages/${messageId}`, { body });
  return data;
}
export async function deleteChatMessage(roomId, messageId) {
  const { data } = await api.delete(`/rooms/${roomId}/chat/messages/${messageId}`);
  return data;
}
export async function toggleChatMessageReaction(roomId, messageId, emoji) {
  const { data } = await api.post(`/rooms/${roomId}/chat/messages/${messageId}/reactions`, { emoji });
  return data;
}
export async function createDm(roomId, recipientIdentity) {
  const { data } = await api.post(`/rooms/${roomId}/chat/dm`, { recipient_identity: recipientIdentity });
  return data;
}
export async function createGroupChat(roomId, memberIdentities, name) {
  const { data } = await api.post(`/rooms/${roomId}/chat/group`, { member_identities: memberIdentities, name });
  return data;
}
export async function uploadChatFile(roomId, file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/rooms/${roomId}/chat/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}
export async function uploadChatGif(roomId, file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/rooms/${roomId}/chat/upload-gif`, form, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}


// Format FastAPI error detail (string | array | object) into a string for UI.
export function fmtErr(detail, fallback = "Something went wrong.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ") || fallback;
  }
  if (typeof detail.msg === "string") return detail.msg;
  if (typeof detail.message === "string") return detail.message;
  return fallback;
}

// R22-MVP — Waiting room self-report and return
export async function selfReportWaitingRoom(roomId, reason = "camera_off") {
  const r = await api.post(`/rooms/${roomId}/waiting-room/self-report`, { reason });
  return r.data;
}

export async function returnFromWaitingRoom(roomId, turnstileToken) {
  const r = await api.post(`/rooms/${roomId}/waiting-room/return`, { turnstile_token: turnstileToken });
  return r.data;
}

// ---- Full Spectrum Center ----
export async function getCenterRooms() {
  const { data } = await api.get("/center/rooms");
  return data;
}

export async function getCenterRoom(roomId) {
  const { data } = await api.get(`/center/rooms/${roomId}`);
  return data;
}

export async function setCenterUsername(username) {
  const { data } = await api.patch("/center/profile", { username });
  return data;
}

export async function updateCenterRoomBrand(roomId, brand) {
  const { data } = await api.patch(`/center/rooms/${roomId}/branding`, brand);
  return data;
}

export async function setCenterRoomAvailability(roomId, is_open) {
  const { data } = await api.patch(`/center/rooms/${roomId}/availability`, { is_open });
  return data;
}

export async function updateCenterRoomSetup(roomId, setup) {
  const { data } = await api.patch(`/center/rooms/${roomId}/setup`, setup);
  return data;
}

export async function getCenterRoomKeyholders(roomId) {
  const { data } = await api.get(`/center/rooms/${roomId}/keyholders`);
  return data;
}

export async function addCenterRoomKeyholder(roomId, username) {
  const { data } = await api.post(`/center/rooms/${roomId}/keyholders`, { username });
  return data;
}

export async function removeCenterRoomKeyholder(roomId, username) {
  const { data } = await api.delete(`/center/rooms/${roomId}/keyholders/${encodeURIComponent(username)}`);
  return data;
}

export async function getRoomDjSignups(roomId) {
  const { data } = await api.get(`/center/rooms/${roomId}/dj-signups`);
  return data;
}

export async function requestRoomDjSlot(roomId, durationMinutes) {
  const { data } = await api.post(`/center/rooms/${roomId}/dj-signups`, { duration_minutes: durationMinutes });
  return data;
}

export async function decideRoomDjSlot(roomId, signupId, status) {
  const { data } = await api.patch(`/center/rooms/${roomId}/dj-signups/${signupId}`, { status });
  return data;
}

export async function cancelRoomDjSlot(roomId, signupId) {
  const { data } = await api.delete(`/center/rooms/${roomId}/dj-signups/${signupId}`);
  return data;
}

export async function getRoomGroups(roomId) {
  const { data } = await api.get(`/center/rooms/${roomId}/groups`);
  return data;
}

export async function createRoomGroup(roomId, name) {
  const { data } = await api.post(`/center/rooms/${roomId}/groups`, { name });
  return data;
}

export async function addRoomGroupMember(roomId, groupId, username) {
  const { data } = await api.post(`/center/rooms/${roomId}/groups/${groupId}/members`, { username });
  return data;
}

export async function removeRoomGroupMember(roomId, groupId, username) {
  const { data } = await api.delete(`/center/rooms/${roomId}/groups/${groupId}/members/${encodeURIComponent(username)}`);
  return data;
}

export async function deleteRoomGroup(roomId, groupId) {
  const { data } = await api.delete(`/center/rooms/${roomId}/groups/${groupId}`);
  return data;
}

export async function uploadCenterMedia(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/center/media", form, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export async function getCenterFeed() {
  const { data } = await api.get("/center/feed");
  return data;
}

export async function createCenterPost(body, media_ids = []) {
  const { data } = await api.post("/center/feed", { body, media_ids });
  return data;
}

export async function toggleCenterPostReaction(postId, emoji) {
  const { data } = await api.post(`/center/posts/${postId}/reactions`, { emoji });
  return data;
}

export async function getRoomCenterFeed(roomId) {
  const { data } = await api.get(`/center/rooms/${roomId}/feed`);
  return data;
}

export async function createRoomCenterPost(roomId, body, media_ids = []) {
  const { data } = await api.post(`/center/rooms/${roomId}/feed`, { body, media_ids });
  return data;
}

export async function getCenterInbox() {
  const { data } = await api.get("/center/inbox");
  return data;
}

export async function sendCenterInboxMessage(recipient_username, body, media_ids = []) {
  const { data } = await api.post("/center/inbox", { recipient_username, body, media_ids });
  return data;
}

export async function markCenterInboxRead(messageId) {
  const { data } = await api.post(`/center/inbox/${messageId}/read`);
  return data;
}

export async function getCenterRoleAssignments() {
  const { data } = await api.get("/center/my-roles");
  return data;
}
